import { executePublicRouteLoader, normalizePublicPath, } from "../../core/index.js";
function splitPublicUrl(rawUrl) {
    const hashIndex = rawUrl.indexOf("#");
    const withoutHash = hashIndex === -1 ? rawUrl : rawUrl.slice(0, hashIndex);
    const hash = hashIndex === -1 ? "" : rawUrl.slice(hashIndex);
    const queryIndex = withoutHash.indexOf("?");
    return {
        pathname: queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex),
        search: queryIndex === -1 ? "" : withoutHash.slice(queryIndex),
        hash,
    };
}
export function toPublicMarkdownPath(site, rawPath) {
    const path = site.normalizePath(rawPath);
    if (site.removeLocalePrefix(path) === "/")
        return path === "/" ? "/index.md" : `${path}.md`;
    return `${path}.md`;
}
export function toPublicMarkdownUrl(site, rawUrl) {
    const parts = splitPublicUrl(rawUrl);
    return `${toPublicMarkdownPath(site, parts.pathname)}${parts.search}${parts.hash}`;
}
function isAbsoluteHttpUrl(value) {
    return /^https?:\/\//i.test(value) || value.startsWith("//");
}
function formatResolvedPublicUrl(url, absolute) {
    return absolute
        ? url.href
        : `${url.pathname}${url.search}${url.hash}`;
}
function markdownText(value) {
    return value
        .replace(/\s+/g, " ")
        .trim()
        .replace(/([\\[\]*_`|])/g, "\\$1");
}
export function formatPublicMarkdownLink(label, href) {
    return `[${markdownText(label)}](<${href.replace(/>/g, "%3E")}>)`;
}
export function resolvePublicMarkdownHref(site, options) {
    const target = options.target.trim();
    if (!target || target.startsWith("#"))
        return options.target;
    let siteBase;
    let resolved;
    try {
        siteBase = new URL(options.siteUrl);
        resolved = new URL(target, new URL(options.currentRoutePath, siteBase));
    }
    catch {
        return options.target;
    }
    if (resolved.protocol !== "http:" && resolved.protocol !== "https:")
        return options.target;
    if (resolved.origin !== siteBase.origin)
        return options.target;
    const absolute = options.absolute ?? isAbsoluteHttpUrl(target);
    if (fromPublicMarkdownPath(resolved.pathname) !== null)
        return formatResolvedPublicUrl(resolved, absolute);
    const matchedRoute = site.getRouteDefinition(resolved.pathname);
    if (!matchedRoute)
        return options.target;
    let routePath = resolved.pathname;
    const targetLocaleCode = site.getLocaleCodeFromPath(routePath);
    if (matchedRoute.localePolicy !== "none" && targetLocaleCode === null) {
        const currentLocaleCode = site.getLocaleCodeFromPath(options.currentRoutePath)
            ?? site.defaultLocaleCode;
        routePath = site.withLocalePrefix(site.removeLocalePrefix(routePath), currentLocaleCode);
    }
    if (!site.isKnownRoute(routePath))
        return options.target;
    resolved.pathname = routePath;
    const representation = options.representation ?? "markdown-if-available";
    if (representation === "markdown-if-available") {
        const route = site.getRouteDefinition(routePath);
        if (route && route.markdown.kind !== "seo") {
            const markdownUrl = toPublicMarkdownUrl(site, `${resolved.pathname}${resolved.search}${resolved.hash}`);
            const markdown = new URL(markdownUrl, siteBase);
            return formatResolvedPublicUrl(markdown, absolute);
        }
    }
    return formatResolvedPublicUrl(resolved, absolute);
}
export function createPublicMarkdownLinkHelpers(options) {
    const href = (target, link) => resolvePublicMarkdownHref(options.site, {
        siteUrl: options.siteUrl,
        currentRoutePath: options.currentRoutePath,
        target,
        ...link,
    });
    return {
        href,
        link: (label, target, link) => formatPublicMarkdownLink(label, href(target, link)),
    };
}
export function fromPublicMarkdownPath(rawPath) {
    const path = normalizePublicPath(rawPath);
    if (path === "/index.md")
        return "/";
    if (!path.endsWith(".md"))
        return null;
    return path.slice(0, -3) || "/";
}
export function fromPublicMarkdownUrl(rawUrl) {
    const parts = splitPublicUrl(rawUrl);
    const pathname = fromPublicMarkdownPath(parts.pathname);
    return pathname === null ? null : `${pathname}${parts.search}${parts.hash}`;
}
export function resolvePublicMarkdownRequest(site, options) {
    const explicitRouteUrl = fromPublicMarkdownUrl(options.requestUrl);
    if (explicitRouteUrl !== null)
        return {
            kind: "markdown",
            mode: "explicit",
            routeUrl: explicitRouteUrl,
            varyAccept: false,
            formatRedirectPath: (path) => toPublicMarkdownUrl(site, path),
        };
    if (options.acceptsMarkdown)
        return {
            kind: "markdown",
            mode: "negotiated",
            routeUrl: options.requestUrl,
            varyAccept: true,
        };
    return {
        kind: "default-representation",
        varyAccept: true,
    };
}
function preservePublicSearch(location, sourceUrl) {
    if (location.includes("?"))
        return location;
    const search = splitPublicUrl(sourceUrl).search;
    return search ? `${location}${search}` : location;
}
function formatPublicRedirect(location, sourceUrl, formatter) {
    const preservedLocation = preservePublicSearch(location, sourceUrl);
    return formatter?.(preservedLocation) ?? preservedLocation;
}
function cleanMarkdownWhitespace(value) {
    return value
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
export function estimateMarkdownTokens(markdown) {
    return Math.max(1, Math.ceil(markdown.split(/\s+/).filter(Boolean).length * 1.35));
}
function createMarkdownResponse(markdown, statusCode, extra = {}) {
    const cleaned = cleanMarkdownWhitespace(markdown);
    return {
        markdown: cleaned,
        statusCode,
        tokenCount: cleaned ? estimateMarkdownTokens(cleaned) : 0,
        cachePolicy: { kind: "no-store" },
        ...extra,
    };
}
function renderFallbackMarkdown(route, options) {
    const title = options.translate(route.seo.titleKey);
    const description = options.translate(route.seo.descriptionKey);
    const url = `${options.siteUrl.replace(/\/$/, "")}${options.routePath}`;
    return `# ${title}\n\n${description}\n\n${url}`;
}
export async function renderPublicMarkdown(options) {
    const routeUrl = options.routeUrl ?? options.routePath;
    const resolution = options.site.resolvePath(options.routePath, {
        redirectLocaleCode: options.redirectLocaleCode,
        redirectStatusCode: options.redirectStatusCode,
    });
    switch (resolution.type) {
        case "redirect":
            return createMarkdownResponse("", resolution.statusCode, {
                redirectTo: formatPublicRedirect(resolution.location, routeUrl, options.formatRedirectPath),
                vary: resolution.vary,
            });
        case "not-found":
            return createMarkdownResponse(options.renderNotFound(), 404);
        case "app-shell":
            return createMarkdownResponse(options.renderNotFound(), 404);
        case "render": {
            const result = await executePublicRouteLoader(resolution, options.loaders ?? {}, {
                signal: options.signal,
                url: routeUrl,
            });
            switch (result.kind) {
                case "not-found":
                    return createMarkdownResponse(options.renderNotFound(), 404);
                case "redirect":
                    return createMarkdownResponse("", result.statusCode, {
                        redirectTo: formatPublicRedirect(result.location, routeUrl, options.formatRedirectPath),
                    });
                case "unavailable":
                    return createMarkdownResponse(options.renderUnavailable(), 503, {
                        retryAfter: result.retryAfter,
                    });
                case "ok": {
                    const links = createPublicMarkdownLinkHelpers({
                        site: options.site,
                        siteUrl: options.siteUrl,
                        currentRoutePath: resolution.routePath,
                    });
                    const context = {
                        ...links,
                        route: resolution.route,
                        routePath: resolution.routePath,
                        locale: options.locale,
                        siteUrl: options.siteUrl,
                        translate: options.translate,
                        data: result.data,
                    };
                    let rendered;
                    switch (resolution.route.markdown.kind) {
                        case "content": {
                            const key = resolution.route.markdown.key;
                            const content = options.getContent?.(key, options.locale);
                            if (content === null || content === undefined)
                                throw new Error(`Missing public Markdown content '${key}' for route '${resolution.route.key}'.`);
                            rendered = content;
                            break;
                        }
                        case "renderer": {
                            const key = resolution.route.markdown.key;
                            const renderer = options.renderers?.[key];
                            if (!renderer)
                                throw new Error(`Missing public Markdown renderer '${key}' for route '${resolution.route.key}'.`);
                            const output = renderer(context);
                            if (output === null || output === undefined)
                                throw new Error(`Public Markdown renderer '${key}' returned no content for route '${resolution.route.key}'.`);
                            rendered = output;
                            break;
                        }
                        case "seo":
                            rendered = renderFallbackMarkdown(resolution.route, options);
                            break;
                    }
                    return createMarkdownResponse(rendered, 200, {
                        cachePolicy: resolution.route.delivery.kind === "spa-shell"
                            ? { kind: "no-store" }
                            : resolution.route.delivery.cache,
                    });
                }
            }
        }
    }
}
//# sourceMappingURL=index.js.map